package org.matthewo.pages.team.tabs

import emotion.react.css
import io.ktor.client.call.*
import io.ktor.client.plugins.resources.*
import js.coroutines.awaitCancellation
import js.objects.buildReadonlyRecord
import js.objects.unsafeJso
import kotlinx.coroutines.launch
import org.matthewo.*
import org.matthewo.api.Event
import org.matthewo.api.TeamData
import org.matthewo.components.YearSelector
import org.matthewo.datasource.CRI
import org.matthewo.datasource.FTCEvents
import org.matthewo.hooks.useCoroutineScope
import org.matthewo.js.toFixed
import org.matthewo.math.toOrdinal
import org.matthewo.pages.event.EVENT_CODE_PARAM
import org.matthewo.pages.event.toEvent
import org.matthewo.pages.team.TEAM_PARAM
import org.matthewo.router.div
import org.matthewo.router.replaceAndNavigate
import org.matthewo.router.toExternal
import org.matthewo.routes.API
import org.matthewo.schema.isCRI
import org.matthewo.schema.sortedByYearDescending
import org.matthewo.styles.TITLE_FONT_COLOR
import org.matthewo.table.MatchesTable
import react.*
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.nav
import react.dom.html.ReactHTML.span
import tanstack.react.router.Link
import tanstack.react.router.useNavigate
import tanstack.react.router.useParams
import tanstack.react.router.useSearch
import tanstack.router.core.NavigateOptions
import tanstack.router.core.RoutePath
import tanstack.router.core.ToOptions
import web.cssom.*
import web.html.HTMLAnchorElement
import web.html.HTMLElement
import web.intersection.IntersectionObserver
import web.scroll.ScrollIntoViewOptions
import web.scroll.ScrollLogicalPosition
import web.scroll.start

const val EVENT_CODE_SEARCH = "event"

val TEAM_PATH = RoutePath(YEAR_PARAM / "team" / TEAM_PARAM)
val TEAM_CONTRIBUTION_PATH = RoutePath(TEAM_PATH / "contribution")
val TEAM_CONTRIBUTION_EVENT_PATH = RoutePath(TEAM_CONTRIBUTION_PATH / EVENT_CODE_PARAM)

fun ToOptions.toTeam(year: Int? = null, teamNumber: Int, eventCode: String? = null) {
    to = TEAM_PATH
    params = buildReadonlyRecord {
        if (null != year) this[YEAR_PARAM] = year.toString()
        this[TEAM_PARAM] = teamNumber.toString()
    }
    if (null != eventCode) {
        search = buildReadonlyRecord {
            this[EVENT_CODE_SEARCH] = eventCode
        }
    }
}

val SeasonEventsTable = FC("SeasonEventTable") {
    val coroutineScope = useCoroutineScope()
    val navigate = useNavigate()
    val pathParams = useParams()
    val searchParams = useSearch()

    var dataWithYear by useState<Pair<Int, TeamData<List<Event>>>>()

    val selectedYear = pathParams[YEAR_PARAM] ?: ""
    val teamNumber = pathParams[TEAM_PARAM] ?: ""

    useEffect(selectedYear, teamNumber) {
        val year = selectedYear.toIntOrNull() ?: return@useEffect
        val teamNumber = teamNumber.toIntOrNull() ?: return@useEffect
        val job = coroutineScope.launch {
            val response = client.get(API.Team(year, teamNumber))
            dataWithYear = year to response.body()
        }
        awaitCancellation {
            job.cancel()
            dataWithYear = null
        }
    }

    var showHeader by useState(false)

    var teamNameElement by useState<HTMLAnchorElement>()
    val teamNameRef = useRefCallback<HTMLAnchorElement> {
        teamNameElement = it
    }
    useEffect(teamNameElement) {
        val element = teamNameElement ?: return@useEffect
        val observer = IntersectionObserver(
            { entries, observer ->
                entries.forEach { entry ->
                    if (entry.target == element) {
                        showHeader = !entry.isIntersecting
                    }
                }
            },
            unsafeJso {
                threshold = [0.4]
            },
        )
        observer.observe(element)
        awaitCancellation {
            observer.unobserve(element)
        }
    }

    val year = dataWithYear?.first
    val data = dataWithYear?.second

    val selectedEvent = useMemo(data?.data) {
        searchParams[EVENT_CODE_SEARCH]?.toString()
    }

    val pageRef = useRefCallback<HTMLElement>(selectedYear, teamNumber) {
        if (null != selectedEvent) return@useRefCallback
        it.scrollIntoView(ScrollIntoViewOptions(inline = ScrollLogicalPosition.start))
    }
    val selectedEventRef = useRefCallback<HTMLElement>(selectedEvent) {
        it.scrollIntoView(ScrollIntoViewOptions(inline = ScrollLogicalPosition.start))
    }

    useLoading(null == dataWithYear)
    useHeader(data, showHeader) {
        val yearsSet = buildSet {
            year?.let { add(it) }
            data?.seasons?.let { addAll(it) }
        }
        val years = yearsSet.sortedByYearDescending { it }
        if (showHeader) ElementHeader(false) {
            Link {
                css {
                    color = TITLE_FONT_COLOR
                    textAlign = TextAlign.center
                    whiteSpace = WhiteSpace.normal
                }
                data?.run { FTCEvents.teamURL(number) }?.let {
                    toExternal(it)
                }
                +(data?.run { "$number - $name" } ?: "")
            }
            YearSelector {
                this.years = years
            }
        } else DefaultHeader(years)
    }

    if (null != data) {
        div {
            ref = pageRef
            css {
                margin = 1.rem
            }
            nav {
                css {
                    fontSize = 3.rem

                    width = 100.pct
                    marginBottom = 1.rem
                    display = Display.flex
                    gap = 0.5.em
                }
                Link {
                    css {
                        display = Display.inlineBlock
                        color = TITLE_FONT_COLOR
                    }
                    ref = teamNameRef
                    toExternal(FTCEvents.teamURL(data.number))
                    +"${data.number} - ${data.name}"
                }
                Link {
                    css {
                        width = 1.5.em
                        minWidth = 1.5.em
                        height = 1.5.em
                        minHeight = 1.5.em
                        display = Display.inlineBlock
                        backgroundImage = url("/graph.png")
                        backgroundSize = BackgroundSize.contain
                        backgroundRepeat = BackgroundRepeat.noRepeat
                    }
                    to = TEAM_CONTRIBUTION_PATH
                }
            }
            data.data.forEach { event ->
                div {
                    if (event.code.lowercase().trim() == selectedEvent?.lowercase()?.trim()) {
                        ref = selectedEventRef
                    }
                    css {
                        marginBottom = 2.rem
                    }
                    nav {
                        css {
                            fontSize = 2.rem

                            width = 100.pct
                            minHeight = 1.5.em
                            display = Display.flex
                            gap = 0.5.em
                        }
                        Link {
                            css {
                                display = Display.inlineBlock
                                color = TITLE_FONT_COLOR
                            }

                            coroutineScope.replaceAndNavigate(navigate, {
                                search = buildReadonlyRecord { this[EVENT_CODE_SEARCH] = event.code }
                            }) {
                                toEvent(code = event.code)
                            }
                            +event.name
                        }
                        val links = buildList<Pair<String, NavigateOptions.() -> Unit>> {
                            if (event.matches.isNotEmpty()) add("/graph.png" to {
                                to = TEAM_CONTRIBUTION_EVENT_PATH
                                params = buildReadonlyRecord {
                                    this[EVENT_CODE_PARAM] = event.code
                                }
                            })
                            when (year?.isCRI) {
                                true ->
                                    add("/chicago.png" to { toExternal(CRI.matchesURL(year, event.code)) })

                                false -> {
                                    add("/scout.png" to { toExternal("https://ftcscout.org/events/$year/${event.code}") })
                                    add("/first.svg" to { toExternal(FTCEvents.eventURL(year, event.code)) })
                                }

                                null -> Unit
                            }
                        }
                        links.forEach { link ->
                            Link {
                                css {
                                    width = 1.5.em
                                    minWidth = 1.5.em
                                    height = 1.5.em
                                    minHeight = 1.5.em
                                    display = Display.inlineBlock
                                    backgroundImage = url(link.first)
                                    backgroundSize = BackgroundSize.contain
                                    backgroundRepeat = BackgroundRepeat.noRepeat
                                    backgroundPosition = GeometryPosition.center
                                }
                                coroutineScope.replaceAndNavigate(navigate, {
                                    search = buildReadonlyRecord { this[EVENT_CODE_SEARCH] = event.code }
                                }) {
                                    link.second(this)
                                }
                            }
                        }
                        if (102024 == year) Link {
                            css {
                                width = 1.5.em
                                minWidth = 1.5.em
                                height = 1.5.em
                                minHeight = 1.5.em

                                display = Display.inlineFlex
                                flexDirection = FlexDirection.column
                                justifyContent = JustifyContent.center
                                alignContent = AlignContent.center
                            }
                            div {
                                css {
                                    width = 100.pct
                                    height = 10.px

                                    backgroundColor = NamedColor.yellow
                                }

                                div {
                                    css {
                                        width = 30.pct
                                        height = 100.pct
                                        margin = Auto.auto
                                        backgroundColor = NamedColor.black
                                    }
                                }
                            }
                            coroutineScope.replaceAndNavigate(navigate, {
                                search = buildReadonlyRecord { this[EVENT_CODE_SEARCH] = event.code }
                            }) {
                                to = INTO_THE_DEEP_CRI_PLACEMENT_VISUALIZATION_PATH
                                params = buildReadonlyRecord {
                                    this[EVENT_CODE_PARAM] = event.code
                                }
                            }
                        }
                    }
                    if (null != event.oprn || null != event.ranking || null != event.playoffs) {
                        div {
                            css {
                                fontSize = 1.rem

                                width = 100.pct
                                display = Display.flex
                                flexDirection = FlexDirection.row
                                gap = 2.em
                            }
                            event.oprn?.let { oprn ->
                                div {
                                    css {
                                        display = Display.grid
                                        gridTemplateRows = repeat(4, 1.fr)
                                        gridTemplateColumns = repeat(2, Auto.auto)
                                        gap = 0.5.em
                                    }
                                    [
                                        "NP Total" to oprn.npTotal.toFixed(1),
                                        "Auto" to oprn.auto.toFixed(1),
                                        "Teleop" to oprn.teleop.toFixed(1),
                                        "Endgame" to oprn.endgame.toFixed(1),
                                    ].forEach { (label, value) ->
                                        span {
                                            +"$label:"
                                        }
                                        span {
                                            +value
                                        }
                                    }
                                }
                            }
                            event.ranking?.let { ranking ->
                                div {
                                    css {
                                        display = Display.grid
                                        gridTemplateRows = repeat(4, 1.fr)
                                        gridTemplateColumns = repeat(2, Auto.auto)
                                        gap = 0.5.em
                                    }
                                    [
                                        "Rank" to "${ranking.rank}",
                                        "W-L-T" to "${ranking.wins}-${ranking.losses}-${ranking.ties}",
                                        "RP" to ranking.rp.toFixed(1),
                                        "TBP1" to ranking.tbp1.toFixed(1),
                                    ].forEach { (label, value) ->
                                        span {
                                            +"$label:"
                                        }
                                        span {
                                            +value
                                        }
                                    }
                                }
                            }
                            event.playoffs?.let { playoffs ->
                                div {
                                    css {
                                        display = Display.grid
                                        gridTemplateRows = repeat(4, 1.fr)
                                        gridTemplateColumns = repeat(2, Auto.auto)
                                        gap = 0.5.em
                                    }
                                    [
                                        "Placed" to (playoffs.placed?.toOrdinal() ?: "N/A"),
                                        "Alliance" to "${playoffs.alliance}",
                                        "Member" to playoffs.member.title,
                                        "NP Mean" to (playoffs.npAverage?.toFixed(1) ?: "N/A"),
                                    ].forEach { (label, value) ->
                                        span {
                                            +"$label:"
                                        }
                                        span {
                                            +value
                                        }
                                    }
                                }
                            }
                        }
                    }
                    if (event.matches.isNotEmpty()) {
                        MatchesTable {
                            this.year = year ?: 0
                            code = event.code
                            matches = event.matches
                            team = data.number
                        }
                    }
                }
            }
        }
    }
}
